<h4 oui-dialog-title>Corriger les personnes physiques</h4>

<oui-dialog-content [scroll]="true">
  <div class="dialog-content">
    @if (step === "select-persons") {
      <section class="dialog-section">
        <div class="dialog-intro">
          <p>
            Sélectionnez les personnes physiques à supprimer. Le choix est
            valide lorsqu'une seule personne reste conservée.
          </p>
          <oui-tag
            type="status"
            size="big"
            [status]="canValidatePersonSelection ? 'completed' : 'pending'"
          >
            {{ remainingPersonCount }} personne(s) conservée(s)
          </oui-tag>
        </div>

        <div class="suggestion-banner">
          <p class="muted">{{ suggestionMessage }}</p>

          <oui-tag type="status" size="big" [status]="suggestionTagStatus">
            {{ initializing ? "Pré-sélection..." : "Pré-sélection" }}
          </oui-tag>
        </div>

        <oui-table class="data-table" [lineSizing]="'dense'">
          <table mat-table [dataSource]="persons">
            <ng-container matColumnDef="delete">
              <th mat-header-cell *matHeaderCellDef>Supprimer</th>
              <td mat-cell *matCellDef="let person">
                <oui-checkbox
                  [checked]="personIdsToDelete.has(person.personId)"
                  (checkedChange)="
                    togglePersonDeletion(person.personId, $event)
                  "
                ></oui-checkbox>
              </td>
            </ng-container>

            <ng-container matColumnDef="identity">
              <th mat-header-cell *matHeaderCellDef>Personne</th>
              <td mat-cell *matCellDef="let person">
                <div class="content-long">
                  <strong>{{ formatPersonLabel(person) }}</strong>
                  <div>
                    {{ formatText(person.personCode) }}
                  </div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="birth">
              <th mat-header-cell *matHeaderCellDef>Naissance</th>
              <td mat-cell *matCellDef="let person">
                <div class="content-short">
                  <div>{{ formatDate(person.birthDate) }}</div>
                  <div>{{ formatText(person.birthCity) }}</div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="contact">
              <th mat-header-cell *matHeaderCellDef>Coordonnées</th>
              <td mat-cell *matCellDef="let person">
                {{ getContactSummary(person) }}
              </td>
            </ng-container>

            <ng-container matColumnDef="relations">
              <th mat-header-cell *matHeaderCellDef>Relations</th>
              <td mat-cell *matCellDef="let person">
                {{ getRelationCount(person) }}
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="personColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: personColumns"></tr>
          </table>
        </oui-table>
      </section>
    }

    @if (step === "select-relations") {
      <section class="dialog-section">
        <div class="dialog-intro">
          <p>
            Les personnes à supprimer ont des relations. Choisissez celles à
            transférer vers
            @if (keptPerson; as kept) {
              {{ formatPersonLabel(kept) }}.
            } @else {
              -.
            }
          </p>
          <oui-tag type="status" size="big" status="pending">
            {{ selectedRelationCount }} /
            {{ transferableRelationCount }} relation(s) cochée(s)
          </oui-tag>
        </div>

        <oui-table class="data-table" [lineSizing]="'dense'">
          <table mat-table [dataSource]="relationRows">
            <ng-container matColumnDef="transfer">
              <th mat-header-cell *matHeaderCellDef>Transférer</th>
              <td mat-cell *matCellDef="let row">
                @if (row.canTransfer) {
                  <oui-checkbox
                    [checked]="row.selectedForTransfer"
                    (checkedChange)="toggleRelationTransfer(row, $event)"
                  ></oui-checkbox>
                } @else {
                  <span class="muted">Suppression</span>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="source">
              <th mat-header-cell *matHeaderCellDef>Depuis</th>
              <td mat-cell *matCellDef="let row">
                {{ formatPersonLabel(row.sourcePerson) }}
              </td>
            </ng-container>

            <ng-container matColumnDef="kind">
              <th mat-header-cell *matHeaderCellDef>Type</th>
              <td mat-cell *matCellDef="let row">{{ row.kind }}</td>
            </ng-container>

            <ng-container matColumnDef="relation">
              <th mat-header-cell *matHeaderCellDef>Relation</th>
              <td mat-cell *matCellDef="let row">
                {{ formatText(row.relationTypeCode) }}
              </td>
            </ng-container>

            <ng-container matColumnDef="relatedPerson">
              <th mat-header-cell *matHeaderCellDef>Personne liée</th>
              <td mat-cell *matCellDef="let row">
                {{ formatRelatedPerson(row) }}
              </td>
            </ng-container>

            <ng-container matColumnDef="arianeId">
              <th mat-header-cell *matHeaderCellDef>ArianeId</th>
              <td mat-cell *matCellDef="let row">
                {{ formatText(row.arianeId) }}
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="relationColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: relationColumns"></tr>
          </table>
        </oui-table>
      </section>
    }

    @if (step === "summary") {
      <section class="dialog-section">
        <div class="summary-block">
          <oui-tag type="status" size="big" status="completed">
            Préparation validée
          </oui-tag>
          <p>
            Personne conservée :
            <strong>
              @if (keptPerson; as kept) {
                {{ formatPersonLabel(kept) }}
              } @else {
                -
              }
            </strong>
          </p>
          <p>
            Personnes à supprimer :
            <strong>
              @for (person of personsToDelete; track person.personId) {
                {{ formatPersonLabel(person) }}{{ $last ? "" : " • " }}
              }
            </strong>
          </p>
          @if (hasRelationsToTransfer) {
            <p>
              Relations sélectionnées pour transfert :
              <strong
                >{{ selectedRelationCount }} /
                {{ transferableRelationCount }}</strong
              >
            </p>
          } @else {
            <p>Aucune relation à transférer n'a été détectée.</p>
          }
          @if (executionError) {
            <p class="error-text">{{ executionError }}</p>
          }
          @if (executionResult; as result) {
            <p>
              Résultat :
              <strong>{{ result.message }}</strong>
            </p>
            <p>
              Personnes supprimées :
              <strong>{{ result.deletedPersonsCount }}</strong> • Relations
              remappées : <strong>{{ result.updatedRelationsCount }}</strong> •
              Relations supprimées :
              <strong>{{ result.deletedRelationsCount }}</strong>
            </p>
            <p class="muted">
              Transaction :
              <strong>{{ result.rolledBack ? "ROLLBACK" : "COMMIT" }}</strong>
            </p>
          }
          <p class="muted">
            @if (executionResult) {
              Exécution terminée.
            } @else {
              La correction sera exécutée côté back après validation finale.
            }
          </p>
        </div>
      </section>
    }
  </div>
</oui-dialog-content>

<oui-dialog-actions>
  @if (step === "select-persons") {
    <oui-button type="text" color="secondary" (click)="close()"
      >Annuler</oui-button
    >
    <oui-button
      iconSuffix="check_circle"
      [disabled]="!canValidatePersonSelection"
      (click)="validatePersonSelection()"
    >
      Valider le choix
    </oui-button>
  }

  @if (step === "select-relations") {
    <oui-button type="text" color="secondary" (click)="backToPersonSelection()">
      Retour
    </oui-button>
    <oui-button iconSuffix="check_circle" (click)="validateRelationSelection()">
      Valider les relations
    </oui-button>
  }

  @if (step === "summary") {
    @if (!executionResult) {
      <oui-button
        type="text"
        color="secondary"
        [disabled]="executing"
        (click)="backFromSummary()"
      >
        Retour
      </oui-button>
      <oui-button
        iconSuffix="play_arrow"
        [disabled]="executing"
        (click)="executeCorrection()"
      >
        {{ executing ? "Exécution..." : "Exécuter la correction" }}
      </oui-button>
    } @else {
      <oui-button
        iconSuffix="close"
        (click)="close(executionResult.success && !executionResult.rolledBack)"
      >
        Fermer
      </oui-button>
    }
  }
</oui-dialog-actions>
